import useSWR from 'swr';
import type { BrandedFaresRequest, BrandedFaresResponse } from '@/app/api/hotel-detail/branded-fares/route';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider.tsx';
import { useSelectedMealPlan } from '@/app/components/hotel-detail/SelectedMealPlanProvider';
import { useInitialParams } from '../InitialParamsProvider';
import { getLoggingInfo } from '../lib/loggingInfo';
import { BASE_PATH } from '../../search/utils/path';
import { fetchWithTracingAndLogging } from '../common/fetchWithTracing';
import { useOffersContext } from '../OffersProvider';
import useUserConfig from './useUserConfig';
import { useUrlSearchParams } from '../UrlSearchParamsProvider';

export function useBrandedFares(transportId: string | undefined, priceIncrement: number | undefined) {
  const initialParams = useInitialParams();
  const { searchInfoData, commonData, localization } = usePageWrapperData();
  const { offersData } = useOffersContext();
  const { selectedMealPlan } = useSelectedMealPlan();
  const { showBackToTravel } = useUserConfig();
  const { totalGuestsWithoutInfants, nights } = useUrlSearchParams();

  const key =
    offersData?.pricingId && selectedMealPlan?.id && transportId
      ? `branded-fares-${offersData?.pricingId}-${selectedMealPlan?.id}-${transportId}`
      : undefined;

  const selectedMealPlanPrice = selectedMealPlan?.finalPrice;

  const selectedTransportPriceIncrement = priceIncrement ?? 0;

  return useSWR<BrandedFaresResponse | null>(
    key,
    async () =>
      fetchWithTracingAndLogging<BrandedFaresResponse>(
        `${BASE_PATH}/api/hotel-detail/branded-fares`,
        {
          method: 'POST',
          headers: {
            'content-type': 'application/json',
          },
          body: JSON.stringify({
            businessProfileId: localization.businessProfile,
            pricingId: offersData?.pricingId,
            rateId: selectedMealPlan?.id,
            saabLog: localization.configValuesApp.saabDebugLogs,
            showBackToTravel,
            selectedMealPlanPrice,
            selectedTransportPriceIncrement,
            priceType: initialParams.priceType,
            totalGuestsWithoutInfants,
            nights,
            transportId,
            localization,
            searchInfoData,
            flightClickId: initialParams.flightClickId || '',
          } as BrandedFaresRequest),
        },
        getLoggingInfo(initialParams.pageType, 'branded-fares', 'useBrandedFares'),
        searchInfoData.searchTraceParent,
        commonData
      ),
    {
      revalidateIfStale: false,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      revalidateOnMount: true,
      dedupingInterval: 1000 * 60 * 30,
    }
  );
}
